import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';
import type {ImageSource} from 'UI/components/modules/ResponsiveImage/ResponsiveImage';

import {useImageSlider} from './useImageSlider';

/** A single image entry for the slider. */
export interface SliderImage {
    /** Alt text for the image. */
    alt?: string;
    /** Focal point X coordinate (0–100). */
    focalX?: number | null;
    /** Focal point Y coordinate (0–100). */
    focalY?: number | null;
    /** Unique identifier. */
    id: string;
    /** Array of responsive image sources for srcset. */
    srcSet?: ImageSource[];
    /** Image source URL (fallback). */
    url: string;
}

/**
 * The props for the `ImageSlider` component.
 * Configures a touch-enabled image slider with dot indicators using keen-slider.
 */
interface ComponentProps {
    /** Auto-play interval in milliseconds. 0 disables auto-play. */
    autoPlayMs?: number;
    /** Position of the dot indicators from the bottom. */
    dotPositionBottom?: string;
    /** Height of the slider container. */
    height?: string;
    /** Array of images to display. */
    images: SliderImage[];
    /** Initial slide index to display. Defaults to `0`. */
    initialSlide?: number;
    /** CSS object-fit value for the images. Defaults to `'cover'`. */
    objectFit?: 'contain' | 'cover' | 'fill' | 'none';
    /** Callback fired when the active slide changes. */
    onSlideChange?(index: number): void;
    /**
     * Marks the initially visible slide as the LCP candidate via `fetchpriority="high"`.
     * Only ever applied to the first slide, since later slides aren't visible on load.
     *
     * @default false
     */
    priority?: boolean;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'ImageSlider'
     */
    testId?: string;
}

/**
 * A touch-enabled image slider powered by keen-slider with dot indicators.
 * When only a single image is provided, it renders a plain image without
 * sliding or indicators. Supports optional auto-play.
 *
 * @param props                   The component props.
 * @param props.autoPlayMs        Auto-play interval in ms. Defaults to `0` (disabled).
 * @param props.height            Container height. Defaults to `'100%'`.
 * @param props.images            Array of images.
 * @param props.initialSlide      Initial slide index to display. Defaults to `0`.
 * @param props.objectFit         CSS object-fit value for the images. Defaults to `'cover'`.
 * @param props.onSlideChange     Callback fired when the active slide changes.
 * @param props.priority          Marks the first slide as the LCP candidate. Defaults to `false`.
 * @param props.testId            An optional test ID used for automation selectors. Defaults to `'ImageSlider'`.
 * @param props.dotPositionBottom Position of the dot indicators from the bottom.
 * @returns A JSX element rendering the ImageSlider.
 *
 * @example
 * ```tsx
 * <ImageSlider images={[{id: '1', url: '/img1.jpg'}, {id: '2', url: '/img2.jpg'}]} />
 * ```
 */
const ImageSlider = ({
    autoPlayMs = 0,
    dotPositionBottom,
    height = '100%',
    images,
    initialSlide = 0,
    objectFit = 'cover',
    onSlideChange,
    priority = false,
    testId = 'ImageSlider'
}: ComponentProps) => {
    const {activeIndex, sliderRef} = useImageSlider(images.length, autoPlayMs, initialSlide, onSlideChange);

    if (images.length === 0) return null;

    if (images.length === 1) {
        return (
            <Wrapper $height={height} data-cy={testId}>
                <ResponsiveImage
                    alt={images[0].alt ?? ''}
                    focalX={images[0].focalX}
                    focalY={images[0].focalY}
                    objectFit={objectFit}
                    priority={priority}
                    sizes="100vw"
                    src={images[0].url}
                    srcSet={images[0].srcSet}
                    testId={`${testId}-image`}
                />
            </Wrapper>
        );
    }

    return (
        <Wrapper $height={height} data-cy={testId}>
            <Track ref={sliderRef} className="keen-slider" data-cy={`${testId}-track`}>
                {images.map((img, index) => (
                    <Slide key={img.id} className="keen-slider__slide">
                        <ResponsiveImage
                            alt={img.alt ?? ''}
                            focalX={img.focalX}
                            focalY={img.focalY}
                            objectFit={objectFit}
                            priority={priority && index === 0}
                            sizes="100vw"
                            src={img.url}
                            srcSet={img.srcSet}
                            testId={`${testId}-image`}
                        />
                    </Slide>
                ))}
            </Track>
            <Dots $bottom={dotPositionBottom ?? spacing(3)} data-cy={`${testId}-dots`}>
                {images.map((img, index) => (
                    <Dot key={img.id} $isActive={index === activeIndex} />
                ))}
            </Dots>
        </Wrapper>
    );
};

ImageSlider.displayName = 'ImageSlider';

export {ImageSlider};

// ── Styled ──────────────────────────────────────────────────────────────

/** Props for the wrapper. */
interface WrapperProps {
    /** The height of the slider container. */
    $height: number | string;
}

const Wrapper = styled.div<WrapperProps>`
    height: ${({$height}) => $height};
    overflow: hidden;
    position: relative;
    width: 100%;
`;

const Track = styled.div`
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    display: flex;
    height: 100%;
    overflow: hidden;
    touch-action: pan-y;
    user-select: none;
    width: 100%;
`;

const Slide = styled.div`
    height: 100%;
    min-height: 100%;
    overflow: hidden;
    position: relative;
    width: 100%;
`;

interface DotsProps {
    /** The height of the slider container. */
    $bottom: number | string;
}

const Dots = styled.div<DotsProps>`
    align-items: center;
    bottom: ${({$bottom}) => $bottom};
    display: flex;
    gap: ${spacing(2)};
    justify-content: center;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    z-index: 1;
`;

/** Props for dot indicators. */
interface DotProps {
    /** Whether this dot is the active one. */
    $isActive: boolean;
}

const Dot = styled.div<DotProps>`
    background: ${({$isActive}) => ($isActive ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.5)')};
    border-radius: 50%;
    height: 0.8rem;
    transition: background 0.2s ease, transform 0.2s ease;
    width: 0.8rem;
`;